/**
 * @file: useReconciliateQuotationForm.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useQuotationContext } from '@euler/app/flows/task/pages/quotation/components/QuotationContext';
import { keyOfIssue } from '@euler/app/flows/task/pages/quotation/functions/utils';
import { QuotationItemType } from '@euler/model/enum';
import {
  QuotationConstructionItemStaged,
  QuotationIssuePayload,
  QuotationMaterialItemStaged,
} from '@euler/model/task-detail';
import { array2map } from '@euler/utils/array';
import { useCallback } from 'react';

export const useReconciliateQuotationForm = () => {
  const {
    taskManager: { quotationManager },
  } = useTaskContext();

  const { catalog } = useQuotationContext();

  return useCallback(
    (selection: string[]) => {
      const templateSubjects =
        catalog.templateConfig?.categories.flatMap(category =>
          category.groups.flatMap(group => group.subjects),
        ) ?? [];
      const templateSubjectMap = array2map(templateSubjects, x => x.name);

      quotationManager.produce(draft => {
        for (const name of selection) {
          const templateSubject = templateSubjectMap.get(name);
          const subjectInfo = catalog.subjectMap.get(name);
          const existing = draft.subjects.find(x => x.name === name);

          if (existing != null) {
            //#region reconcile issues of subject
            for (let i = existing.issues.length - 1; i >= 0; i--) {
              const existingIssue = existing.issues[i];
              const issue = subjectInfo?.issues.find(
                x => keyOfIssue(x) === keyOfIssue(existingIssue),
              );
              if (issue == null) {
                existing.issues.splice(i, 1);
              } else if (!issueEquals(existingIssue, issue)) {
                existing.issues[i] = issue;
              }
            }
            if (subjectInfo?.issues != null) {
              for (const issue of subjectInfo.issues) {
                if (
                  !existing.issues.find(
                    x => keyOfIssue(x) === keyOfIssue(issue),
                  )
                ) {
                  existing.issues.push(issue);
                }
              }
            }
            //#endregion
            continue;
          }

          draft.subjects.push({
            name,
            issues: subjectInfo?.issues ?? [],
            included: true,
            items:
              templateSubject != null
                ? templateSubject.items.map<QuotationConstructionItemStaged>(
                    item => ({
                      name: item.name,
                      code: item.code,
                      type: QuotationItemType.Construction,
                      price: item.manHourCost,
                      discountPrice: item.discountManHourCost,
                      qty: 1,
                      discountRemark: item.discountRemark,
                      defaultIncluded: item.included ?? false,
                      included: Boolean(
                        item.included || templateSubject.items.length === 1,
                      ),
                      materials:
                        item.materials?.map<QuotationMaterialItemStaged>(
                          material => ({
                            type: QuotationItemType.Material,
                            name: material.name,
                            brandName: material.brandName,
                            partNumber: material.partNumber,
                            inventoryStatus: material.inventoryStatus,
                            materialType: material.type,
                            price: material.price,
                            discountPrice: material.discountPrice,
                            qty: material.qty,
                            optionGroup: material.optionGroup,
                            defaultIncluded: material.included ?? false,
                            included:
                              Boolean(
                                item.included ||
                                  templateSubject.items.length === 1,
                              ) &&
                              Boolean(
                                material.included ||
                                  item.materials?.length === 1,
                              ),
                            discountRemark: material.discountRemark,
                          }),
                        ) ?? [],
                    }),
                  )
                : subjectInfo?.items != null
                ? subjectInfo.items.map<QuotationConstructionItemStaged>(
                    item => ({
                      type: QuotationItemType.Construction,
                      name: item.name,
                      price: undefined,
                      discountPrice: undefined,
                      qty: 1,
                      included: true,
                      defaultIncluded: true,
                      materials: [],
                    }),
                  )
                : [],
          });
        }
        for (let i = draft.subjects.length - 1; i >= 0; i--) {
          const subject = draft.subjects[i];
          if (!selection.includes(subject.name)) {
            draft.subjects.splice(i, 1);
          }
        }
      });
    },
    [catalog.subjectMap, catalog.templateConfig?.categories, quotationManager],
  );
};

const issueEquals = (a: QuotationIssuePayload, b: QuotationIssuePayload) => {
  return JSON.stringify(a) === JSON.stringify(b);
};
